import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { CompanyRole } from '@iota/iota-management-portal-v4-client';
import { Store } from '@ngrx/store';
import { map, Observable, Subscription } from 'rxjs';
import { companiesByRoles } from 'src/app/store/reducer/company.reducer';
import {
    sortByNameWithAlphabeticCharactersFirst,
    getCompanyName,
    getFirstDeactivatedCompanyIndex,
    isCompanyDeactivated,
    sortCompaniesByDeactivationStatus
} from '../../utils';
import { CompanyEntity } from 'src/app/store/entity-models';

const NO_COMPANY_FILTER = 'NO_COMPANY';
// Placeholder value that represents the "All active customers" pseudo-option in the
// form control. Its presence drives the option's checkbox state in the dropdown.
const ALL_ACTIVE_CUSTOMERS = 'ALL_ACTIVE_CUSTOMERS';

export interface CompanyFilterResult {
    filteredCompanyIds: string[];
    noCompanyFilter: boolean;
}

@Component({
    standalone: false,
    selector: 'app-company-filter',
    templateUrl: './company-filter.component.html',
    styleUrls: ['./company-filter.component.scss']
})
export class CompanyFilterComponent implements OnInit, OnDestroy {
    noCompanyFilterValue = NO_COMPANY_FILTER;
    allActiveCustomersValue = ALL_ACTIVE_CUSTOMERS;

    @Input() roles = Object.values(CompanyRole);
    @Input() allowNoCompanyFilter = true;
    @Input() allowAllActiveCustomersFilter = false;
    @Input() multiple = true;
    @Input() placeholder = $localize`Select customer...`;
    @Input() panelClass = '';

    @Input() selectStyle = 'select-bordered';

    @Output() filterChange = new EventEmitter<CompanyFilterResult>();
    @Output() applyFiltersEvent = new EventEmitter<void>();

    companies$!: Observable<CompanyEntity[]>;

    companyFilter = new UntypedFormControl([]);

    private latestActiveCompanies: CompanyEntity[] = [];

    // mat-select's (selectionChange) only tells us the new value, not which option the
    // user toggled. We remember the previous state of the "All active customers" option
    // so we can tell its click apart from an individual-company toggle.
    private allActiveCustomersWasSelected = false;

    private subscriptions = new Subscription();

    constructor(private store: Store) {}

    ngOnInit(): void {
        this.companies$ = this.store.select(companiesByRoles(this.roles)).pipe(
            map((companies) => sortByNameWithAlphabeticCharactersFirst(companies)),
            map((companies) => companies.filter((company) => !company.roles.includes(CompanyRole.VENDOR))),
            map((companies) => sortCompaniesByDeactivationStatus(companies))
        );

        if (this.allowAllActiveCustomersFilter) {
            this.subscriptions.add(
                this.companies$.subscribe((companies) => {
                    this.latestActiveCompanies = companies.filter((company) => !isCompanyDeactivated(company));
                    this.syncAllActiveCustomersSelection();
                })
            );
        }
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    onCompanyFilterDropdownClosed(isOpen: boolean): void {
        if (!isOpen) {
            this.applyFiltersEvent.emit();
        }
    }

    updateCompanyFilter() {
        if (this.allowAllActiveCustomersFilter) {
            this.syncAllActiveCustomersSelection();
        }

        const filter = this.selectedValues;

        this.filterChange.emit({
            filteredCompanyIds: filter.filter((c): c is CompanyEntity => !!(c as CompanyEntity)?.companyId).map((c) => c.companyId),
            noCompanyFilter: filter.includes(NO_COMPANY_FILTER)
        });
    }

    resetCompanyFilter() {
        this.companyFilter.setValue(this.multiple ? [] : null);
        this.allActiveCustomersWasSelected = false;
        this.updateCompanyFilter();
    }

    getCompanyFilterName(company: string | CompanyEntity) {
        if (company === this.noCompanyFilterValue) {
            return $localize`No affiliation`;
        }
        if (company === this.allActiveCustomersValue) {
            return $localize`All active customers`;
        }

        const comp = company as CompanyEntity;
        const isDeactivated = this.isCompanyDeactivated(comp);
        const deactivatedLabel = isDeactivated ? $localize` (deactivated)` : '';
        return comp?.name + ' (' + comp?.companyNumber + ')' + deactivatedLabel;
    }

    get firstSelectionLabel(): string {
        const firstSelection = this.selectedValues[0];
        return firstSelection ? this.getCompanyFilterName(firstSelection as string | CompanyEntity) : '';
    }

    /**
     * Number of additional selections beyond the first label shown in the trigger.
     * When "All active customers" is selected, the active companies are already
     * represented by that label, so they are excluded from the count.
     */
    get additionalSelectionCount(): number {
        const value = this.selectedValues;
        if (value.includes(ALL_ACTIVE_CUSTOMERS)) {
            return value.filter((v) => v !== ALL_ACTIVE_CUSTOMERS && !this.latestActiveCompanies.includes(v as CompanyEntity)).length;
        }
        return Math.max(0, value.length - 1);
    }

    get additionalSelectionLabel(): string {
        const count = this.additionalSelectionCount;
        if (count < 1) return '';
        return count === 1
            ? $localize`:@@companyFilter.more.one:(+${count} more)`
            : $localize`:@@companyFilter.more.multiple:(+${count} more)`;
    }

    getCompanyName = getCompanyName;
    getFirstDeactivatedIndex = getFirstDeactivatedCompanyIndex;
    isCompanyDeactivated = isCompanyDeactivated;

    /**
     * Keeps the "All active customers" option and the individual active-company selections consistent.
     *
     * - If the user just clicked the "All active customers" option, mirror its new state onto every
     *   active company (select or deselect them all).
     * - Otherwise an individual company was toggled, so update the "All active customers" option to
     *   appear checked iff every active company is currently selected.
     */
    private syncAllActiveCustomersSelection(): void {
        const value = this.selectedValues;
        const isSelected = value.includes(ALL_ACTIVE_CUSTOMERS);
        const userClickedOption = isSelected !== this.allActiveCustomersWasSelected;

        let nextValue = value;
        if (userClickedOption) {
            const valueWithoutActivesAndOption = value.filter(
                (v) => v !== ALL_ACTIVE_CUSTOMERS && !this.latestActiveCompanies.includes(v as CompanyEntity)
            );
            // The marker goes first so it drives the trigger's first label when active companies are selected.
            nextValue = isSelected
                ? [ALL_ACTIVE_CUSTOMERS, ...this.latestActiveCompanies, ...valueWithoutActivesAndOption]
                : valueWithoutActivesAndOption;
        } else {
            const shouldBeSelected = this.areAllActiveCompaniesSelected(value);
            if (shouldBeSelected !== isSelected) {
                nextValue = shouldBeSelected ? [ALL_ACTIVE_CUSTOMERS, ...value] : value.filter((v) => v !== ALL_ACTIVE_CUSTOMERS);
            }
        }

        if (nextValue !== value) {
            this.companyFilter.setValue(nextValue);
        }
        this.allActiveCustomersWasSelected = this.selectedValues.includes(ALL_ACTIVE_CUSTOMERS);
    }

    private get selectedValues(): unknown[] {
        const value = this.companyFilter.value;
        if (value === null || value === undefined) return [];
        return Array.isArray(value) ? value : [value];
    }

    private areAllActiveCompaniesSelected(value: unknown[]): boolean {
        return this.latestActiveCompanies.length > 0 && this.latestActiveCompanies.every((c) => value.includes(c));
    }
}
